import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';
import { RequestIdColumnComponent } from '@components/data-grid/cell-components/request-id-column/request-id-column.component';
import { map, Observable } from 'rxjs';

import { DataConnector, LongStringColumnComponent, RequestStatusColumnComponent } from '@components/data-grid';
import { DateColumnComponent } from '@components/data-grid/cell-components/date-column/date-column.component';
import { FilterHelper } from '@components/data-grid/filter/filter.helper';
import { environment } from '@environments';
import {
  ClassificationRequest,
  ConditionPropertyOperator,
  Country,
  DataResponse,
  EndUse,
  MAX_COLUMN_WIDTH,
  OnBehalfOfRequester,
  PageSizeOptions,
  Region,
  RequestCategory,
  Requester,
  RequestIdFilterItem,
  RequestStatus,
  RequestType,
  RowDataDefinition,
  Subdivision,
} from '@models';
import { FieldType, FilterCriterion, FilterDropdownOption } from '@models/filter.model';
import { CompanySettingsService } from '@services/company-settings/company-settings.service';
import {
  getFormattedEnumKeyByValue,
  mapEntityIdListToFilterOptions,
  mapListToFilterOptions,
} from '@utils';
import { ColDef, ICellRendererParams } from 'ag-grid-community';

@Injectable({
  providedIn: 'root',
})
export class ClassificationHelperService {
  private static readonly CLASSIFICATION_REQUEST_URL = '/Request/get';
  private static readonly CLASSIFICATION_REQUEST_DEATAILS_URL = '/Request';
  static readonly DEFAULT_PARAMS = {
    pageSize: PageSizeOptions[0].value,
    pageNumber: 1,
    sortExpression: `createdOn desc`,
  };
  private static readonly CLASSIFICATION_REQUESTER_FILTER_URL = '/Request/requester-filter';
  private static readonly CLASSIFICATION_REQUEST_CATEGORY_FILTER_URL = '/Request/request-category-filter';
  private static readonly CLASSIFICATION_REQUEST_FILTER_URL = '/Request/classification-requests';

  readonly #http: HttpClient = inject(HttpClient);
  #companySettingsService = inject(CompanySettingsService);
  #requestCategoryEnabled = this.#companySettingsService.requestCategory;

  public readonly filterCriteria = signal(this.initializeFilterCriteria());

  /**
   * Fetches the list of countries for classification filtering.
   * Sends a POST request to the API with pagination and sorting.
   *
   * @returns {Observable<DataResponse<Country>>} Observable emitting the country data response.
   */
  public countriesList(search = ''): Observable<DataResponse<Country>> {
    const url = `${environment.apiConfig.baseUrl}/country/filter`;
    const params = {
      pageSize: 500,
      pageNumber: 1,
      sortExpression: 'name asc',
      search,
    };
    return this.#http.post<DataResponse<Country>>(url, params);
  }

  public countriesOptions(search = ''): Observable<FilterDropdownOption[]> {
    return this.countriesList(search).pipe(
      map(response =>
        mapListToFilterOptions(response.items, country => ({
          displayName: country.name,
          value: country.id,
        }))
      )
    );
  }

  public classificationCountriesList(search = ''): Observable<DataResponse<Country>> {
    const url = `${environment.apiConfig.baseUrl}/country/classification`;
    const params = {
      pageSize: 500,
      pageNumber: 1,
      sortExpression: 'name asc',
      search,
    };
    return this.#http.post<DataResponse<Country>>(url, params);
  }

  public classificationCountriesOptions(search = ''): Observable<FilterDropdownOption[]> {
    return this.classificationCountriesList(search).pipe(
      map(response =>
        mapListToFilterOptions(response.items, country => ({
          displayName: country.name,
          value: country.id,
        }))
      )
    );
  }

  public endUseList(): Observable<DataResponse<EndUse>> {
    const url = `${environment.apiConfig.baseUrl}/end-use/filter`;
    const params = {
      pageSize: 500,
      pageNumber: 1,
      sortExpression: 'name asc',
    };
    return this.#http.post<DataResponse<EndUse>>(url, params);
  }

  public subdivisionList(search = ''): Observable<DataResponse<Subdivision>> {
    const url = `${environment.apiConfig.baseUrl}/subdivision/filter`;
    const params = {
      pageSize: 500,
      pageNumber: 1,
      sortExpression: 'name asc',
      search,
    };
    return this.#http.post<DataResponse<Subdivision>>(url, params);
  }

  public subdivisionOptions(search = ''): Observable<FilterDropdownOption[]> {
    return this.subdivisionList(search).pipe(
      map(response =>
        mapListToFilterOptions(response.items, subdivision => ({
          displayName: subdivision.name,
          value: subdivision.id,
        }))
      )
    );
  }

  public activeSubdivisionList(search = ''): Observable<DataResponse<Subdivision>> {
    const url = `${environment.apiConfig.baseUrl}/subdivision/active`;
    const params = {
      pageSize: 500,
      pageNumber: 1,
      sortExpression: 'name asc',
      search,
    };
    return this.#http.post<DataResponse<Subdivision>>(url, params);
  }

  public activeSubdivisionOptions(search = ''): Observable<FilterDropdownOption[]> {
    return this.activeSubdivisionList(search).pipe(
      map(response =>
        mapListToFilterOptions(response.items, subdivision => ({
          displayName: subdivision.name,
          value: subdivision.id,
        }))
      )
    );
  }

  public regionsList(search = ''): Observable<DataResponse<Region>> {
    const url = `${environment.apiConfig.baseUrl}/region`;
    const params = {
      pageSize: 500,
      pageNumber: 1,
      sortExpression: 'name asc',
      search,
    };
    return this.#http.post<DataResponse<Region>>(url, params);
  }

  public regionOptions(search = ''): Observable<FilterDropdownOption[]> {
    return this.regionsList(search).pipe(
      map(response =>
        mapListToFilterOptions(response.items, region => ({
          displayName: region.name,
          value: region.id,
        }))
      )
    );
  }

  public requestCategoriesList(search = ''): Observable<DataResponse<RequestCategory>> {
    const url = `${environment.apiConfig.baseUrl}/request-categories`;
    const params = {
      pageSize: 500,
      pageNumber: 1,
      sortExpression: 'name asc',
      search,
      filterGroups: [],
    };
    return this.#http.post<DataResponse<RequestCategory>>(url, params);
  }

  public requestCategoryOptions(search = ''): Observable<FilterDropdownOption[]> {
    return this.requestCategoriesList(search).pipe(
      map(response =>
        response.items.map(
          (category, index): FilterDropdownOption => ({
            id: index,
            displayName: category.name,
            value: category.requestCategoryId,
            key: category.requestCategoryId,
          })
        )
      )
    );
  }

  public onBehalfOfRequestersList(search = '', requesterId?: string): Observable<DataResponse<OnBehalfOfRequester>> {
    const url = `${environment.apiConfig.baseUrl}/create-request/obo-requesters`;
    const params = {
      pageSize: 100,
      pageNumber: 1,
      sortExpression: 'fullName asc',
      search,
      filterGroups: [
        {
          logicalGroupOperator: 0,
          entityPropertyFilters: [
            {
              propertyName: 'id',
              conditionPropertyOperator: 1,
              value: requesterId,
            },
          ],
        },
      ],
    };
    return this.#http.post<DataResponse<OnBehalfOfRequester>>(url, params);
  }

  public onBehalfOfRequesterOptions(search = '', requesterId?: string): Observable<FilterDropdownOption[]> {
    return this.onBehalfOfRequestersList(search, requesterId).pipe(
      map(response =>
        response.items.map(
          (requester, index): FilterDropdownOption => ({
            id: index,
            displayName: requester.fullname,
            value: requester.id,
            key: requester.id,
          })
        )
      )
    );
  }

  /**
   * Fetch classification requests from the API.
   * @param params Optional query parameters.
   * @returns Observable of classification requests.
   */
  public getClassificationRequests(params?: Params): Observable<DataResponse<ClassificationRequest>> {
    const url = `${environment.apiConfig.baseUrl}${ClassificationHelperService.CLASSIFICATION_REQUEST_URL}`;
    return this.#http.post<DataResponse<ClassificationRequest>>(url, this.buildRequestParams(params));
  }

  public getClassificationRequestFilterList(params?: Params): Observable<FilterDropdownOption[]> {
    const url = `${environment.apiConfig.baseUrl}${ClassificationHelperService.CLASSIFICATION_REQUEST_FILTER_URL}`;
    return this.#http
      .post<DataResponse<RequestIdFilterItem>>(url, this.buildRequestParams(params))
      .pipe(map(response => mapEntityIdListToFilterOptions(response.items, request => request.requestId)));
  }

  /**
   * Fetch classification requesters from the API.
   * @param params Optional query parameters.
   * @returns Observable of requesters.
   */
  public getClassificationRequesterFilterList(params?: Params): Observable<FilterDropdownOption[]> {
    const url = `${environment.apiConfig.baseUrl}${ClassificationHelperService.CLASSIFICATION_REQUESTER_FILTER_URL}`;
    return this.#http.post<DataResponse<Requester>>(url, this.buildRequestParams(params)).pipe(
      map(response =>
        response.items.map(
          requester =>
            ({
              displayName: requester.fullname,
              key: requester.id,
              value: requester.id,
            }) as FilterDropdownOption
        )
      )
    );
  }

  public getClassificationRequestCategoryFilterList(params?: Params): Observable<DataResponse<RequestCategory>> {
    const url = `${environment.apiConfig.baseUrl}${ClassificationHelperService.CLASSIFICATION_REQUEST_CATEGORY_FILTER_URL}`;
    return this.#http.post<DataResponse<RequestCategory>>(url, this.buildRequestParams(params));
  }

  private buildRequestParams(params?: Params): Params {
    const p = DataConnector.buildRequestParams(params);
    const result: Params = {
      pageSize: p?.['pageSize'] || ClassificationHelperService.DEFAULT_PARAMS.pageSize,
      pageNumber: p?.['pageNumber'] || ClassificationHelperService.DEFAULT_PARAMS.pageNumber,
      sortExpression: p?.['sortExpression'] || ClassificationHelperService.DEFAULT_PARAMS.sortExpression,
    };

    if (p?.['filterGroups'] && p['filterGroups'].length > 0) {
      result['filterGroups'] = p['filterGroups'];
    }

    if (p?.['search'] && p['search'].trim() !== '') {
      result['search'] = p['search'];
    }

    return result;
  }

  public getRequestDetails(requestEntityId: string): Observable<ClassificationRequest> {
    const url = `${environment.apiConfig.baseUrl}${ClassificationHelperService.CLASSIFICATION_REQUEST_DEATAILS_URL}/${requestEntityId}`;
    return this.#http.get<ClassificationRequest>(url, { headers: { 'Content-Type': 'application/json' } });
  }
  /**
   * Maps a classification request to a row data definition.
   * @param classificationRequest The classification request to map.
   * @returns The mapped row data definition.
   */
  public mapClassificationRequest({
    requestEntityId,
    requestId,
    type,
    requestCategory,
    requestStatus,
    classifiedProducts,
    classifiedItems,
    requester,
    requesterEntityId,
    createdOn,
    requestComment,
  }: ClassificationRequest): RowDataDefinition {
    return {
      requestEntityId,
      requestId,
      type,
      requestCategory,
      requestStatus,
      classifiedProducts,
      classifiedItems,
      requester,
      requesterEntityId,
      createdOn: new Date(createdOn),
      requestComment,
    };
  }

  /**
   * Generates column definitions for the classification grid.
   * @returns Array of column definitions.
   */

  public getAllColumns = computed(() => {
    const columns = this.getColumnDefinitions();

    if (!this.#requestCategoryEnabled()) {
      this.#removeColumnByField(columns, 'requestCategory');
    }
    return columns;
  });

  public getColumnDefinitions(): ColDef[] {
    return [
      DataConnector.createGridColumn({
        field: 'requestId',
        headerName: 'Request ID',
        cellRenderer: RequestIdColumnComponent,
        width: 130,
      }),
      DataConnector.createGridColumn({
        field: 'type',
        headerName: 'Request Type',
        cellRenderer: (params: ICellRendererParams) => getFormattedEnumKeyByValue(RequestType, params.value),
        width: 130,
      }),
      DataConnector.createGridColumn({
        field: 'requestCategory',
        headerName: 'Request Category',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'requestCategory' },
        width: 150,
      }),
      DataConnector.createGridColumn({
        field: 'requestStatus',
        headerName: 'Status',
        cellRenderer: RequestStatusColumnComponent,
        width: 170,
      }),
      DataConnector.createGridColumn({
        field: 'classifiedProducts',
        headerName: 'Classified Products',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'classifiedProducts' },
        width: 150,
      }),
      DataConnector.createGridColumn({
        field: 'classifiedItems',
        headerName: 'Classified Items',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'classifiedItems' },
        width: 130,
      }),
      DataConnector.createGridColumn({
        field: 'requester',
        headerName: 'Requester',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'requester' },
        width: 180,
      }),
      DataConnector.createGridColumn({
        field: 'createdOn',
        headerName: 'Requested Date',
        cellRenderer: DateColumnComponent,
        width: 180,
      }),
      DataConnector.createGridColumn({
        field: 'requestComment',
        headerName: 'Comment',
        cellRenderer: LongStringColumnComponent,
        cellRendererParams: { colName: 'requestComment' },
        maxWidth: MAX_COLUMN_WIDTH,
      }),
    ];
  }

  #removeColumnByField(columns: ColDef[], field: string) {
    const requestCategoryIndex = columns.findIndex(col => col.field === field);
    if (requestCategoryIndex > -1) {
      columns.splice(requestCategoryIndex, 1);
    }
  }

  #removeFilterByField(filters: FilterCriterion[], field: string) {
    const requestCategoryIndex = filters.findIndex(col => col.field === field);
    if (requestCategoryIndex > -1) {
      filters.splice(requestCategoryIndex, 1);
    }
  }

  public initializeFilterCriteria(params: Params = {}): FilterCriterion[] {
    const filters = [
      FilterHelper.createFilterCriterion({
        key: 'id',
        label: 'Request ID',
        placeholder: 'Select Request ID',
        field: 'id',
        type: FieldType.MultipleLookup,
        operation: ConditionPropertyOperator.In,
        optionsRequest$: (search: string) =>
          this.getClassificationRequestFilterList({
            pageSize: 100,
            pageNumber: 1,
            sortExpression: 'requestId asc',
            search,
          }),
      }),
      FilterHelper.createFilterCriterion({
        key: 'type',
        label: 'Request Type',
        placeholder: 'Select Request Type',
        field: 'type',
        type: FieldType.MultipleSelect,
        operation: ConditionPropertyOperator.In,
        options: DataConnector.generateFilterOptionsFromEnum(RequestType),
      }),
      FilterHelper.createFilterCriterion({
        key: 'requestCategory',
        label: 'Request Category',
        placeholder: 'Select Request Category',
        field: 'requestCategoryEntityId',
        type: FieldType.Select,
        operation: ConditionPropertyOperator.Equal,
        options: params['requestCategoryOptions'],
      }),
      FilterHelper.createFilterCriterion({
        key: 'requestStatus',
        label: 'Status',
        placeholder: 'Select Status',
        field: 'requestStatus',
        type: FieldType.MultipleSelect,
        operation: ConditionPropertyOperator.In,
        options: DataConnector.generateFilterOptionsFromEnum(RequestStatus),
      }),
      FilterHelper.createFilterCriterion({
        key: 'requester',
        label: 'Requester',
        placeholder: 'Select Requester',
        field: 'requesterEntityId',
        type: FieldType.Lookup,
        operation: ConditionPropertyOperator.Equal,
        optionsRequest$: (search: string) =>
          this.getClassificationRequesterFilterList({
            pageSize: 100,
            pageNumber: 1,
            sortExpression: 'fullName asc',
            search,
          }),
        access: params['access'],
      }),
      FilterHelper.createFilterCriterion({
        key: 'createdOnFrom',
        label: 'Requested Date: From',
        placeholder: 'Select Requested Date: From',
        field: 'createdOn',
        type: FieldType.Date,
        operation: ConditionPropertyOperator.OnOrAfter,
        dateOptions: {
          maxDateField: 'createdOnTo',
        },
      }),
      FilterHelper.createFilterCriterion({
        key: 'createdOnTo',
        label: 'Requested Date: To',
        placeholder: 'Select Requested Date: To',
        field: 'createdOn',
        type: FieldType.Date,
        operation: ConditionPropertyOperator.OnOrBefore,
        dateOptions: {
          minDateField: 'createdOnFrom',
        },
      }),
    ];

    if (!this.#requestCategoryEnabled()) {
      this.#removeFilterByField(filters, 'requestCategoryEntityId');
    }

    return filters;
  }
}
